<template>
  <section class="stores-why">
    <GridContainer padding>
      <div class="stores-why__panel">
        <p class="stores-why__eyebrow">Deine Vorteile</p>
        <div class="stores-why__panel-intro">
          <Heading
            tag="h2"
            size="lg"
            spaces-bottom-rem
          >
            Das hast du, wenn du per Handyrechnung zahlst.
          </Heading>

          <Paragraph
            is-size-big
            class="stores-why__panel-intro-text"
          >
            Ein Bezahlweg für alle Stores: voller Cashback auf den ersten Kauf, kinderleichte
            Einrichtung, volle Kontrolle und Transparenz auf der Rechnung.
          </Paragraph>
        </div>

        <div class="stores-why__grid">
          <!-- 1. Bei hunderten Apps & Games dabei -->
          <Card
            v-bind="card1"
            class="stores-why__card stores-why__card--marquee"
          >
            <template #image>
              <DcbLandingpageLogoWall
                class="stores-why__logowall"
                :rows="logoWall3Rows"
              />
            </template>
          </Card>

          <!-- 2. Wofür du per Handyrechnung zahlst -->
          <Card
            v-bind="card2"
            class="stores-why__card stores-why__card--show"
          >
            <template #heading>
              <div class="stores-why__windmill">
                <div
                  class="stores-why__windmill-step"
                  :class="{ 'stores-why__windmill-step--active': currentWindmillStep === 1 }"
                >
                  <span class="stores-why__windmill-num">1</span>
                  <p class="stores-why__windmill-steptext">Zuerst fürs Cashback registrieren</p>
                </div>
                <div
                  class="stores-why__windmill-step"
                  :class="{ 'stores-why__windmill-step--active': currentWindmillStep === 2 }"
                >
                  <span class="stores-why__windmill-num">2</span>
                  <p class="stores-why__windmill-steptext">Handyrechnung im Store hinterlegen</p>
                </div>
                <div
                  class="stores-why__windmill-step"
                  :class="{ 'stores-why__windmill-step--active': currentWindmillStep === 3 }"
                >
                  <span class="stores-why__windmill-num">3</span>
                  <p class="stores-why__windmill-steptext">Einmal per SMS-Code bestätigen</p>
                </div>
                <div
                  class="stores-why__windmill-step"
                  :class="{ 'stores-why__windmill-step--active': currentWindmillStep === 4 }"
                >
                  <span class="stores-why__windmill-num">4</span>
                  <p class="stores-why__windmill-steptext">Kaufen, Cashback kommt automatisch</p>
                </div>
              </div>
            </template>
            <template #imageBackground>
              <CdnImage
                class="stores-why__pay-phone stores-why__pay-phone--first"
                :class="{
                  'stores-why__pay-phone--active': currentWindmillStep === 1,
                  'stores-why__pay-phone--past': currentWindmillStep === 2,
                }"
                src="/content/content-images/dcb/stores/why/a65dfd792c753658d952fef4a543e433/01-mockup-form.png"
                aspect-ratio="151 / 304"
                sizes="xs:320px sm:420px"
                alt=""
                lazy
                render-without-figure
              />
              <CdnImage
                class="stores-why__pay-phone stores-why__pay-phone--second"
                :class="{
                  'stores-why__pay-phone--active': currentWindmillStep === 2,
                  'stores-why__pay-phone--past': currentWindmillStep === 3,
                }"
                src="/content/content-images/dcb/stores/why/be90b0555ca649ae4670fb4da1b8292d/02-mockup-ios-add-method.png"
                aspect-ratio="151 / 304"
                sizes="xs:320px sm:420px"
                alt=""
                lazy
                render-without-figure
              />
              <CdnImage
                class="stores-why__pay-phone stores-why__pay-phone--third"
                :class="{
                  'stores-why__pay-phone--active': currentWindmillStep === 3,
                  'stores-why__pay-phone--past': currentWindmillStep === 4,
                }"
                src="/content/content-images/dcb/stores/why/d3a8530cc5ddb0c1eb275652987ce4e6/03-mockup-3-otp.png"
                aspect-ratio="151 / 304"
                sizes="xs:320px sm:420px"
                alt=""
                lazy
                render-without-figure
              />
              <CdnImage
                class="stores-why__pay-phone stores-why__pay-phone--fourth"
                :class="{
                  'stores-why__pay-phone--active': currentWindmillStep === 4,
                  'stores-why__pay-phone--past': currentWindmillStep === 1,
                }"
                src="/content/content-images/dcb/stores/why/f590e2fb343a30794ff5280b912796f7/04-mockup-cashback-rechnung-appstore.png"
                aspect-ratio="151 / 304"
                sizes="xs:320px sm:420px"
                alt=""
                lazy
                render-without-figure
              />
            </template>
          </Card>

          <!-- 3. Stat cycler -->
          <Card
            class="stores-why__card stores-why__card--media"
            theme="black"
            size="small"
          >
            <template #background>
              <CdnImage
                class="dcb-lp-why__media-bg"
                src="/content/content-images/dcb/landingpage/a3c7eda7ccf934568fb3cbb2ac054d2e/brand-kv-movie-1080.jpg"
                aspect-ratio="1 / 1"
                alt=""
                lazy
                render-without-figure
              />
              <div
                class="dcb-lp-why__media-scrim"
                aria-hidden="true"
              />
            </template>
            <div class="stores-why__media-overlay">
              <Heading
                tag="h3"
                size="md"
                class="stores-why__card-title"
                spaces-bottom-rem
              >
                Längst Alltag in Deutschland
              </Heading>
              <Paragraph class="stores-why__card-text">
                Millionen Menschen bezahlen längst einfach per Handyrechnung.
              </Paragraph>
              <div
                class="stores-why__statcycle"
                aria-hidden="true"
              >
                <span
                  v-for="(stat, index) in stats"
                  :key="stat.value"
                  class="stores-why__statcycle-item"
                  :class="{ 'is-active': index === activeStat }"
                >
                  <strong>{{ stat.value }}</strong>
                  <span>{{ stat.label }}</span>
                </span>
              </div>
            </div>
          </Card>

          <!-- 4. Du behältst die Kontrolle -->
          <Card
            class="stores-why__card stores-why__card--trust"
            size="small"
          >
            <div
              class="stores-why__trustlogos"
              aria-hidden="true"
            >
              <span class="stores-why__trust-badge stores-why__trust-badge--lock">
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="1.8"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                >
                  <rect
                    x="4"
                    y="10"
                    width="16"
                    height="11"
                    rx="2.4"
                  />
                  <path d="M8 10V7a4 4 0 0 1 8 0v3" />
                  <circle
                    cx="12"
                    cy="15.4"
                    r="1.5"
                    fill="currentColor"
                    stroke="none"
                  />
                </svg>
              </span>
              <span class="stores-why__trust-badge stores-why__trust-badge--tk">
                <SvgIcon
                  :image="telekomLogoWhite"
                  no-icon-class
                />
              </span>
            </div>
            <Heading
              tag="h3"
              size="md"
              class="stores-why__card-title"
              spaces-bottom-rem
            >
              Du behältst die Kontrolle
            </Heading>
            <Paragraph class="stores-why__card-text">
              Deine Zahlung läuft über die sichere Telekom Infrastruktur, ohne Daten an den Anbieter
              und jederzeit selbst steuerbar.
            </Paragraph>
            <ul class="stores-why__feats">
              <li>
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  aria-hidden="true"
                >
                  <rect
                    x="2"
                    y="5"
                    width="20"
                    height="14"
                    rx="2"
                  />
                  <path d="M2 10h20" />
                  <line
                    x1="3"
                    y1="3"
                    x2="21"
                    y2="21"
                  />
                </svg>
                <span>Keine Konto- oder Kartendaten an den Anbieter</span>
              </li>
              <li>
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  aria-hidden="true"
                >
                  <path d="M12 2 4 5v6c0 5 3.5 8.5 8 11 4.5-2.5 8-6 8-11V5l-8-3z" />
                  <path d="m9 12 2 2 4-4" />
                </svg>
                <span>Geprüft und geschützt</span>
              </li>
              <li>
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  aria-hidden="true"
                >
                  <line
                    x1="4"
                    y1="8"
                    x2="20"
                    y2="8"
                  />
                  <circle
                    cx="9"
                    cy="8"
                    r="2.6"
                    fill="currentColor"
                  />
                  <line
                    x1="4"
                    y1="16"
                    x2="20"
                    y2="16"
                  />
                  <circle
                    cx="15"
                    cy="16"
                    r="2.6"
                    fill="currentColor"
                  />
                </svg>
                <span>Volle Kontrolle über Partnerdienste</span>
              </li>
              <li>
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  aria-hidden="true"
                >
                  <path d="M7 3h10a2 2 0 0 1 2 2v16l-3-2-2 2-2-2-2 2-2-2-3 2V5a2 2 0 0 1 2-2z" />
                  <line
                    x1="9"
                    y1="8"
                    x2="15"
                    y2="8"
                  />
                  <line
                    x1="9"
                    y1="12"
                    x2="15"
                    y2="12"
                  />
                </svg>
                <span>Alle Käufe transparent auf der Telekom Rechnung</span>
              </li>
            </ul>
          </Card>

          <!-- 5. Du weißt, wer abrechnet -->
          <Card
            v-bind="card5"
            class="stores-why__card stores-why__card--bill"
          >
            <template #image>
              <div
                class="stores-why__billwrap"
                aria-hidden="true"
              >
                <div class="stores-why__billmock">
                  <div class="stores-why__billmock-head">
                    <SvgIcon
                      :image="telekomLogo"
                      no-icon-class
                      class="stores-why__billmock-logo"
                    />
                    <span class="stores-why__billmock-meta">
                      <b>Telekom Mobilfunk</b>
                      <i>Rechnung Juni 2026</i>
                    </span>
                  </div>
                  <ul class="stores-why__billmock-lines">
                    <li v-reveal>
                      <span>MagentaMobil Tarif</span>
                      <b>39,95&nbsp;€</b>
                    </li>
                    <li
                      v-reveal
                      class="stores-why__billmock-group"
                      data-bill-step="1"
                    >
                      <SvgIcon
                        :image="telekomLogo"
                        no-icon-class
                      />
                      <span>Telekom · Bezahlen per Handyrechnung</span>
                    </li>
                    <li
                      v-reveal
                      class="stores-why__billmock-sub"
                      data-bill-step="2"
                    >
                      <span>App Store / In-App-Kauf</span>
                      <b>9,99&nbsp;€</b>
                    </li>
                    <li
                      v-reveal
                      class="stores-why__billmock-sub"
                      data-bill-step="3"
                    >
                      <span>Cashback 1. Kauf</span>
                      <b>− 9,99&nbsp;€</b>
                    </li>
                  </ul>
                  <div
                    v-reveal
                    class="stores-why__billmock-total"
                    data-bill-step="4"
                  >
                    <span>Gesamtbetrag</span>
                    <b>39,95&nbsp;€</b>
                  </div>
                </div>
              </div>
            </template>
          </Card>
        </div>
      </div>
    </GridContainer>
  </section>
</template>

<script setup lang="ts">
import {
  GridContainer,
  Heading,
  Paragraph,
  Card,
  type CardComponentProps,
} from '@i22team/td-longtail-vue-components';
import CdnImage from '~/components/cdn-image.vue';
import SvgIcon from '~/components/svg-icon.vue';
import telekomLogo from '~/assets/images/telekom-logo.svg';
import telekomLogoWhite from '~/assets/images/telekom-logo-magenta-bg.svg';
import { logoWall3Rows } from '~/components/dcb/stores/app-icons';

const stats = [
  { value: 'Millionen', label: 'zahlen längst per Handyrechnung' },
  { value: '< 2 Min.', label: 'bis die Zahlart eingerichtet ist' },
  { value: '100 %', label: 'Cashback auf deinen ersten Kauf' },
];
const activeStat = ref(0);

let statTimer: ReturnType<typeof setInterval> | undefined;
let windMillTimer: ReturnType<typeof setTimeout> | undefined;

const startStatCycle = () => {
  statTimer = setInterval(() => {
    activeStat.value = (activeStat.value + 1) % stats.length;
  }, 2800);
};
const stopStatCycle = () => {
  clearInterval(statTimer);
  statTimer = undefined;
};
const handleVisibilityChange = () => {
  stopStatCycle();
  if (!document.hidden) startStatCycle();
};

const card1: CardComponentProps = {
  theme: 'magenta',
  imagePosition: 'bottom',
  size: 'small',
  label: {
    customClass: 'text--uppercase text--bold',
    text: 'Wofür du per Handyrechnung zahlst',
  },
  heading: {
    tag: 'h3',
    size: 'md',
    text: 'Eine Bezahlart, hunderte Apps & Games.',
    spacesBottomRem: true,
  },
  text: 'Vom In-App-Kauf über App-Abos bis Apple Arcade und Google Play Pass, alles per Handyrechnung.',
};

const card2: CardComponentProps = {
  theme: 'white',
  imagePosition: 'bottom',
  size: 'small',
  label: {
    customClass: 'text--uppercase text--primary text--bold',
    text: 'Kinderleicht eingerichtet',
  },
};

const card5: CardComponentProps = {
  size: 'small',
  heading: {
    tag: 'h3',
    size: 'md',
    text: 'Du weißt, wer abrechnet',
    spacesBottomRem: true,
  },
  text: 'Anbieter und Betrag stehen klar als „Telekom · Bezahlen per Handyrechnung“ auf deiner Rechnung, kein anonymer Zahldienst.',
};

const currentWindmillStep = ref(1);

const windmillAnimation = () => {
  if (currentWindmillStep.value >= 4) {
    currentWindmillStep.value = 1;
  } else {
    currentWindmillStep.value++;
  }

  windMillTimer = setTimeout(windmillAnimation, 2000);
};

onMounted(() => {
  startStatCycle();
  windmillAnimation();
  document.addEventListener('visibilitychange', handleVisibilityChange);
});

onUnmounted(() => {
  stopStatCycle();
  if (windMillTimer) {
    clearTimeout(windMillTimer);
    windMillTimer = undefined;
  }
  document.removeEventListener('visibilitychange', handleVisibilityChange);
});
</script>

<style scoped lang="scss">
@use 'assets/base' as *;

.stores-why {
  padding-bottom: 3rem;
  background-color: var(--color-gray-100);

  @include mq($mq-md) {
    padding-bottom: 4rem;
  }

  &__eyebrow {
    font-size: 15px;
    font-weight: 700;
    text-transform: uppercase;
    color: #c20063;
    margin-bottom: 12px;
  }

  &__panel {
    background: var(--color-gray-100);
    border-radius: clamp(20px, 3vw, 32px);
    padding: clamp(1rem, 2vw, 1.5rem) clamp(1rem, 2vw, 1.5rem) clamp(2.5rem, 5vw, 5.5rem);
  }

  &__panel-intro {
    margin-bottom: 2rem;
    display: grid;
    @include mq($mq-md) {
      grid-template-columns: 1fr 1fr;
    }
  }
  &__panel-intro-text {
    @include mq($mq-md) {
      max-width: 56ch;
    }
  }
  &__grid {
    display: grid;
    grid-template-columns: 100%;
    gap: 1rem;

    @include mq($mq-md) {
      grid-template-columns: repeat(12, 1fr);
    }
  }

  &__card-text {
    opacity: 0.85;
  }

  // ---------------------------------------------------------------
  // Card 1: Bei hunderten Apps & Games dabei
  // ---------------------------------------------------------------
  &__card--marquee {
    --card-image-container-height: auto;
    @include mq($mq-md) {
      grid-column: span 7;
    }
  }

  &__logowall {
    --logo-wall-img-w: 100%;
    --logo-wall-img-h: 100%;
  }

  // ---------------------------------------------------------------
  // Card 2: Wofür du per Handyrechnung zahlst
  // ---------------------------------------------------------------
  &__card--show {
    --card-image-container-height: clamp(310px, 28vw, 403px);
    --card-image-background-height: auto;
    --card-image-background-width: clamp(205px, 23vw, 280px);

    @include mq($mq-md) {
      grid-column: span 5;
      min-height: clamp(420px, 34vw, 560px);
    }

    :deep(.card__content) {
      margin-inline: auto;
      color: rgba(255, 255, 255, 0.8);
      max-width: 40ch;
    }
  }

  &__windmill {
    padding-block: 1rem;
    position: relative;
    height: 50px;

    &-step {
      position: absolute;
      display: flex;
      gap: 0.8rem;
      align-items: center;
      opacity: 0;
      transition: opacity 0.25s;

      &--active {
        opacity: 1;
      }
    }

    &-num {
      width: 40px;
      height: 40px;
      color: var(--color-secondary);
      font-weight: 800;
      font-size: 1.2rem;
      display: flex;
      align-items: center;
      justify-content: center;
      flex: 0 0 auto;
      border-radius: 50%;
      background: var(--color-primary);
    }
    &-steptext {
      font-size: 1.4rem;
      line-height: 0.9;
      font-weight: 700;
    }
  }

  &__pay-phone {
    position: absolute;
    left: 50%;
    top: 5%;
    border-radius: 14px;
    transform-origin: 50% 130%;
    transform: translateX(-50%) rotate(108deg);
    opacity: 0;
    transition:
      transform 0.85s cubic-bezier(0.5, 0, 0.2, 1),
      opacity 0.55s;

    @include mq($mq-md) {
      top: 0;
    }

    &--active {
      transform: translateX(-50%) rotate(0deg);
      opacity: 1;
    }

    &--past {
      transform: translateX(-50%) rotate(-108deg);
    }
  }

  @media (prefers-reduced-motion: reduce) {
    .stores-why__pay-phone {
      transition: none;
      opacity: 1;
      filter: none;

      &--first {
        transform: translateX(-80%) rotate(-15deg) translateY(10%);
      }

      &--second {
        transform: translateX(-60%) rotate(-5deg) translateY(10%);
      }

      &--third {
        transform: translateX(-40%) rotate(5deg) translateY(10%);
      }
      &--fourth {
        transform: translateX(-20%) rotate(15deg) translateY(10%);
      }
    }
  }

  // ---------------------------------------------------------------
  // Card 3: Stat cycler
  // ---------------------------------------------------------------
  &__card--media {
    --card-container-content-flex: 0 1 auto;
    --card-container-justify: flex-end;
    min-height: clamp(300px, 40vw, 360px);

    @include mq($mq-md) {
      grid-column: span 4;
    }

    :deep(.card__background:after) {
      content: '';
      position: absolute;
      top: 0;
      right: 0;
      bottom: 0;
      left: 0;
      background: linear-gradient(
        0deg,
        rgba(7, 5, 11, 0.88) 0%,
        rgba(7, 5, 11, 0.42) 46%,
        rgba(7, 5, 11, 0.04) 100%
      );
    }
  }

  &__media-overlay {
    position: relative;
  }

  &__statcycle {
    position: relative;
    display: block;
    width: 100%;
    height: clamp(76px, 8vw, 94px);
    margin-top: 1rem;
  }

  &__statcycle-item {
    position: absolute;
    inset: 0;
    display: flex;
    flex-direction: column;
    justify-content: center;
    gap: 0.25rem;
    padding: clamp(0.85rem, 1.4vw, 1.1rem) clamp(1rem, 1.8vw, 1.4rem);
    border-radius: 16px;
    background: rgba(255, 255, 255, 0.12);
    border: 1px solid rgba(255, 255, 255, 0.22);
    backdrop-filter: saturate(160%) blur(14px);
    -webkit-backdrop-filter: saturate(160%) blur(14px);
    opacity: 0;
    transform: translate(18px, 14px) scale(0.98);
    transition:
      opacity 0.6s ease,
      transform 0.6s ease;
    pointer-events: none;

    &.is-active {
      opacity: 1;
      transform: translate(0, 0) scale(1);
    }

    strong {
      font-size: clamp(1.6rem, 1.1rem + 1.8vw, 2.4rem);
      font-weight: 700;
      color: #fff;
    }

    span {
      font-size: 14px;
      line-height: 1.35;
      color: rgba(255, 255, 255, 0.82);
    }

    @media (prefers-reduced-motion: reduce) {
      transition: opacity 0.25s linear;
      transform: none;
    }
  }

  // ---------------------------------------------------------------
  // Card 4: Du behältst die Kontrolle
  // ---------------------------------------------------------------
  &__card--trust {
    --card-bg-color: #fff;

    @include mq($mq-md) {
      grid-column: span 4;
    }
  }

  &__trustlogos {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    margin-bottom: 1rem;
  }

  &__trust-badge {
    display: flex;
    align-items: center;
    justify-content: center;
    width: clamp(52px, 6.5vw, 72px);
    aspect-ratio: 1;
    border-radius: 14px;
    animation-duration: 8s;
    animation-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
    animation-iteration-count: infinite;

    &--lock,
    &--tk {
      overflow: hidden;
      background: var(--color-primary);
    }

    &--lock {
      color: #fff;
      animation-name: stores-why-trust-lock;

      svg {
        width: 54%;
        height: 54%;
      }
    }

    &--tk {
      animation-name: stores-why-trust-tk;

      :deep(svg) {
        width: 100%;
        height: 100%;
      }
    }
  }

  @keyframes stores-why-trust-tk {
    0%,
    2.5% {
      opacity: 0;
      transform: translateX(calc(-100% - 0.75rem)) scale(0.5);
      filter: blur(8px);
    }
    15% {
      opacity: 1;
      transform: translateX(calc(-100% - 0.75rem)) scale(1);
      filter: blur(0);
    }
    30% {
      opacity: 1;
      transform: translateX(0) scale(1);
      filter: blur(0);
    }
    87.5% {
      opacity: 1;
      transform: translateX(0) scale(1);
      filter: blur(0);
    }
    95%,
    100% {
      opacity: 0;
      transform: translateX(0) scale(0.92);
      filter: blur(0);
    }
  }

  @keyframes stores-why-trust-lock {
    0%,
    25% {
      opacity: 0;
      transform: scale(0.5);
      filter: blur(8px);
    }
    37.5% {
      opacity: 1;
      transform: scale(1);
      filter: blur(0);
    }
    87.5% {
      opacity: 1;
      transform: scale(1);
      filter: blur(0);
    }
    95%,
    100% {
      opacity: 0;
      transform: scale(0.92);
      filter: blur(0);
    }
  }

  @media (prefers-reduced-motion: reduce) {
    .stores-why__trust-badge {
      animation: none;
      opacity: 1;
      transform: none;
      filter: none;
    }
  }

  &__feats {
    list-style: none;
    padding: 0;
    margin: 0;
    display: flex;
    flex-direction: column;
    gap: 0.75rem;

    li {
      display: flex;
      align-items: center;
      gap: 10px;
      font-size: 18px;
      font-weight: 600;
      line-height: 1.35;
    }

    svg {
      flex-shrink: 0;
      width: 34px;
      height: 34px;
      padding: 7px;
      border-radius: 10px;
      background: rgba(226, 0, 116, 0.1);
      color: var(--color-primary);
    }
  }

  // ---------------------------------------------------------------
  // Card 5: Du weißt, wer abrechnet
  // ---------------------------------------------------------------
  &__card--bill {
    --card-bg-color: #f6f6f6;
    --card-image-flex: 1 1 100%;

    @include mq($mq-md) {
      grid-column: span 4;
    }

    .stores-why__card-title {
      margin: 1.25rem 0 0.5rem;
    }
  }

  &__billwrap {
    position: relative;
    margin-bottom: 0.75rem;
  }

  &__billmock {
    --zig-w: 16px;
    --zig-h: 10px;

    position: relative;
    background: #fff;
    padding: clamp(1rem, 1.8vw, 1.4rem) clamp(0.95rem, 1.8vw, 1.25rem);
    font-size: clamp(0.7rem, 1vw, 0.82rem);

    &::before,
    &::after {
      content: '';
      position: absolute;
      left: 0;
      right: 0;
      height: var(--zig-h);
      background-color: #f6f6f6;
    }

    &::before {
      top: calc(-1 * var(--zig-h));
      background-image: conic-gradient(from 135deg at top, #ffffff 25%, transparent 0);
      background-size: var(--zig-w) var(--zig-h);
      background-repeat: repeat-x;
      background-position: 0 0;
    }

    &::after {
      bottom: calc(-1 * var(--zig-h));
      background-image: conic-gradient(from -45deg at bottom, #ffffff 25%, transparent 0);
      background-size: var(--zig-w) var(--zig-h);
      background-repeat: repeat-x;
      background-position: 0 100%;
    }
  }

  &__billmock-head {
    display: flex;
    align-items: center;
    gap: 0.6rem;
    padding-bottom: 0.6rem;
    border-bottom: 1px solid var(--color-gray-200);
  }

  &__billmock-logo {
    width: 26px;
    height: 26px;
    border-radius: 6px;
    flex: 0 0 auto;
  }

  &__billmock-meta {
    display: flex;
    flex-direction: column;
    line-height: 1.25;

    b {
      color: var(--color-font);
      font-weight: 700;
    }

    i {
      font-style: normal;
      color: var(--color-gray--800);
      font-size: 0.9em;
    }
  }

  &__billmock-lines {
    list-style: none;
    margin: 0.6rem 0 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: 0.45rem;

    li {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 0.5rem;
      color: var(--color-gray--800);
    }

    b {
      color: var(--color-font);
      font-weight: 700;
    }
  }

  &__billmock-group {
    margin-top: 0.25rem;
    padding: 0.4rem 0.5rem;
    border-radius: 8px;
    background: rgba(226, 0, 116, 0.08);
    justify-content: flex-start !important;

    :deep(svg) {
      width: 16px;
      height: 16px;
      border-radius: 4px;
    }

    span {
      font-weight: 700;
      color: var(--color-primary);
    }
  }

  &__billmock-sub {
    padding-left: 0.65rem;
    border-left: 2px solid rgba(226, 0, 116, 0.35);
    margin-left: 0.5rem;
  }

  &__billmock-total {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-top: 0.7rem;
    padding-top: 0.6rem;
    border-top: 1px solid var(--color-gray-200);
    font-weight: 700;
    color: var(--color-font);

    b {
      color: var(--color-primary);
    }
  }
}
</style>
